import React, { useRef, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Printer, X, FileSignature } from 'lucide-react';
import { format } from 'date-fns';

export default function CustomerQuote({ project, onTotalChange }) {
  const signatureCanvasRef = useRef(null);
  const displayCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState(false);
  const [signatureDataURL, setSignatureDataURL] = useState(null);

  const { sections = [], jumps = [], parameters = {}, pricing = {} } = project;

  // Calculations
  const totalLightingFeet = sections.reduce((sum, s) => sum + (s.length_feet || 0), 0);
  const totalJumpFeet = jumps.reduce((sum, j) => sum + (j.length_feet || 0), 0);
  const totalWireFeet = totalLightingFeet + totalJumpFeet;
  const spacingInches = parameters.light_spacing_inches || 12;
  const totalPucks = Math.ceil((totalLightingFeet * 12) / spacingInches);
  const transformerWatts = parameters.transformer_watts || 200;
  const wattsPerPuck = 0.5;
  const totalWatts = totalPucks * wattsPerPuck;
  const transformersNeeded = Math.ceil(totalWatts / (transformerWatts * 0.8));

  // Labor time calculation (in hours) - matches MaterialsList detailed breakdown
  const setupTime = 1.0; // Setup & Planning
  const sectionInstallTime = sections.length * 0.5; // Section Installation
  const puckInstallTime = (totalPucks / 10) * 0.25; // Puck mounting (15 min per 10 pucks)
  const jumpTime = jumps.length * 0.5; // Jump wire runs (30 min per jump)
  const totalLaborHours = setupTime + sectionInstallTime + puckInstallTime + jumpTime;

  // Pricing calculations
  const laborCost = totalLaborHours * (pricing.labor_rate_per_hour || 85);
  const lightsCost = totalPucks * (pricing.light_cost_per_unit || 3.5);
  const wireCost = totalWireFeet * (pricing.wire_cost_per_foot || 0.75);
  const transformerCost = transformersNeeded * (pricing.transformer_cost || 150);
  const miscCost = pricing.misc_materials || 50;
  const subtotal = laborCost + lightsCost + wireCost + transformerCost + miscCost;
  const discount = subtotal * ((pricing.discount_percent || 0) / 100);
  const total = subtotal - discount;

  // Notify parent of total changes
  useEffect(() => {
    if (onTotalChange && total > 0) {
      onTotalChange(total);
    }
  }, [total, onTotalChange]);

  useEffect(() => {
    if (signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signatureMode]);

  useEffect(() => {
    if (signatureDataURL && displayCanvasRef.current) {
      const canvas = displayCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = signatureDataURL;
    }
  }, [signatureDataURL]);

  const getCanvasCoords = (e) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCanvasCoords(e);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCanvasCoords(e);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureDataURL(null);
  };

  const saveSignature = () => {
    if (signatureCanvasRef.current && hasSignature) {
      const dataURL = signatureCanvasRef.current.toDataURL('image/png');
      setSignatureDataURL(dataURL);
      setSignatureMode(false);
    }
  };

  const handlePrintQuote = () => {
    window.print();
  };

  // Customer name from project or customer_name field
  const customerName = project.customer_name || project.name || 'Customer';

  return (
    <div className="space-y-6">
      {/* Print Buttons */}
      <div className="flex gap-3 print:hidden">
        <Button onClick={handlePrintQuote} className="bg-amber-600 hover:bg-amber-700">
          <Printer className="w-4 h-4 mr-2" />
          Print Quote
        </Button>
        <Button 
          onClick={() => setSignatureMode(!signatureMode)} 
          variant="outline"
          className="border-slate-300"
        >
          <FileSignature className="w-4 h-4 mr-2" />
          {signatureMode ? 'Close Signature' : 'Get Customer Signature'}
        </Button>
      </div>

      {/* Signature Capture Mode */}
      {signatureMode && (
        <Card className="border-2 border-amber-500 print:hidden">
          <CardHeader className="bg-amber-50">
            <CardTitle className="text-lg">Customer Signature</CardTitle>
            <p className="text-sm text-slate-600">Have the customer sign below with their finger or stylus</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="border-2 border-slate-300 rounded-lg bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={600}
                  height={200}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={clearSignature} variant="outline" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Clear Signature
                </Button>
                {hasSignature && (
                  <Button onClick={saveSignature} size="sm" className="bg-green-600 hover:bg-green-700">
                    Accept Signature
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quote Content */}
      <Card className="print:shadow-none print:border-2">
        <CardHeader className="bg-amber-600 text-white print:bg-white print:text-slate-900 print:border-b-2">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <img 
                src="/SNO-LightingLogo.png" 
                alt="SNO-Lighting Logo" 
                className="h-12 w-auto"
              />
              <CardTitle className="text-2xl">Customer Quote</CardTitle>
            </div>
            <p className="text-sm print:text-slate-600">Valid for 30 days</p>
          </div>
        </CardHeader>
        <CardContent className="p-8 space-y-6">
          {/* Project & Customer Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500">Project</p>
              <p className="font-semibold text-lg">{project.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Address</p>
              <p className="font-semibold">{project.address || 'Not specified'}</p>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-2">Customer Information</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500">Name</p>
                <p className="font-medium">{customerName}</p>
              </div>
              {project.customer_phone && (
                <div>
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="font-medium">{project.customer_phone}</p>
                </div>
              )}
              {project.customer_email && (
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium">{project.customer_email}</p>
                </div>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>
                  <p className="font-medium">Installation Labor</p>
                  <p className="text-sm text-slate-500">Professional installation of permanent lighting</p>
                </TableCell>
                <TableCell className="text-right">{totalLaborHours.toFixed(1)} hrs</TableCell>
                <TableCell className="text-right">${(pricing.labor_rate_per_hour || 85).toFixed(2)}</TableCell>
                <TableCell className="text-right font-medium">${laborCost.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <p className="font-medium">{parameters.light_type?.replace(/_/g, ' ').toUpperCase() || 'RGBW Puck'} Lights</p>
                  <p className="text-sm text-slate-500">At {spacingInches}" spacing</p>
                </TableCell>
                <TableCell className="text-right">{totalPucks} pcs</TableCell>
                <TableCell className="text-right">${(pricing.light_cost_per_unit || 3.5).toFixed(2)}</TableCell>
                <TableCell className="text-right font-medium">${lightsCost.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <p className="font-medium">Wiring ({parameters.wire_gauge?.toUpperCase() || '18 AWG'})</p>
                  <p className="text-sm text-slate-500">Includes lighting runs and extensions</p>
                </TableCell>
                <TableCell className="text-right">{totalWireFeet.toFixed(1)} ft</TableCell>
                <TableCell className="text-right">${(pricing.wire_cost_per_foot || 0.75).toFixed(2)}</TableCell>
                <TableCell className="text-right font-medium">${wireCost.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <p className="font-medium">{transformerWatts}W Transformer</p>
                  <p className="text-sm text-slate-500">Low voltage power supply</p>
                </TableCell>
                <TableCell className="text-right">{transformersNeeded}</TableCell>
                <TableCell className="text-right">${(pricing.transformer_cost || 150).toFixed(2)}</TableCell>
                <TableCell className="text-right font-medium">${transformerCost.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <p className="font-medium">Mounting Hardware & Supplies</p>
                  <p className="text-sm text-slate-500">Track, connectors, sealant, fasteners</p>
                </TableCell>
                <TableCell className="text-right">1</TableCell>
                <TableCell className="text-right">${(pricing.misc_materials || 50).toFixed(2)}</TableCell>
                <TableCell className="text-right font-medium">${miscCost.toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between py-2 text-green-600">
                  <span>Discount ({pricing.discount_percent}%)</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between py-3 text-xl">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-amber-600">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Signature Section */}
          <div className="mt-8 pt-6 border-t-2 border-dashed">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-slate-500 mb-2">Customer Signature</p>
                {signatureDataURL ? (
                  <div className="border-2 border-slate-300 rounded p-2">
                    <canvas 
                      ref={displayCanvasRef}
                      width={600}
                      height={200}
                      className="w-full"
                    />
                  </div>
                ) : (
                  <div className="h-20 border-b-2 border-slate-300" />
                )}
                <p className="text-sm text-slate-500 mt-2">Date: {format(new Date(), 'MMM d, yyyy')}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Installer Signature</p>
                <div className="h-20 border-b-2 border-slate-300" />
                <p className="text-sm text-slate-500 mt-2">Date: _______________</p>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="text-xs text-slate-500 space-y-1 pt-4 border-t">
            <p className="font-medium">Terms & Conditions:</p>
            <p>• Quote valid for 30 days from date above</p>
            <p>• 50% deposit required to schedule installation</p>
            <p>• Final payment due upon completion</p>
            <p>• Warranty covers materials and workmanship for 1 year</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
